<template>
  <div class="shopping-cart-total">
    <template v-if="isFormSubmitted">
      <app-notifier
        v-for="(error, index) in errors"
        :key="index"
        type="failed"
        :data="error"
        class="shopping-cart-total__checkout-error"
      />
    </template>
    <div class="shopping-cart-total__header iw-group-title p-b-20">
      <h2>Summary</h2>
      <a v-if="readOnly || isConfirmation" href="#" @click.prevent="showOrderItems">
        View Items
        <svg xmlns="http://www.w3.org/2000/svg" width="15" height="12" viewBox="0 0 26 15">
          <g fill="none" fill-rule="evenodd">
            <g stroke="currentColor" stroke-width="2">
              <g>
                <path d="M8 0L0 7.5 8 15" transform="translate(-8 -76) translate(16 76)" />
              </g>
            </g>
          </g>
        </svg>
      </a>
    </div>
    <div class="shopping-cart-total__item-container">
      <div v-if="getPromoAmount && (readOnly || isConfirmation)" class="iw-body-text-small shopping-cart-total__item promo_item">
        <p>Promo</p>
        <p>- {{ prependUsdSign(getPromoAmount) }}</p>
      </div>
      <div v-if="getOAPIDiscounts > 0" class="iw-body-text-small shopping-cart-total__item discount_item">
        <p>Discounts</p>
        <p class="iw-item-price">- {{ prependUsdSign(getOAPIDiscounts) }}</p>
      </div>
      <div class="iw-body-text-small shopping-cart-total__item subtotal_item">
        <p>Subtotal</p>
        <p>{{ prependUsdSign(subTotal) }}</p>
      </div>

      <div v-if="tax > 0" class="iw-body-text-small shopping-cart-total__item tax_item">
        <p>Tax</p>
        <p>{{ prependUsdSign(tax) }}</p>
      </div>

      <div v-if="fee > 0">
        <div v-if="APIType === OAPI_API_TYPE">
          <div
            v-for="fee in OAPIFeesDetail"
            :key="fee.id"
            class="shopping-cart-total__fee_item fee_item iw-body-text-small shopping-cart-total__item"
          >
            <p>{{ fee.name }}</p>
            <p>{{ prependUsdSign(fee.value) }}</p>
          </div>
        </div>
        <div v-else class="shopping-cart-total__fee_item fee_item iw-body-text-small shopping-cart-total__item">
          <p>{{ getFeeText }}</p>
          <p>{{ prependUsdSign(fee) }}</p>
        </div>
      </div>
      <div v-if="enableCharityRounding">
        <round-up-for-charity :is-confirmation="isConfirmation" :read-only="readOnly" />
      </div>

      <div v-if="tipEnabled" role="group" aria-labelledby="shopping-cart-total__tip-title">
        <div class="iw-body-text-small shopping-cart-total__item tip_item">
          <p id="shopping-cart-total__tip-title">Tip</p>
          <p>{{ prependUsdSign(selectedTip) }}</p>
        </div>
        <div class="iw-body-text-small shopping-cart-total__item tip_item m-t-4">
          <template v-if="!isConfirmation && !readOnly && !defaultTipEnabled">
            <tip-input v-model="selectedTip"> </tip-input>
          </template>

          <template v-if="!isConfirmation && !readOnly && defaultTipEnabled">
            <default-tip-input v-model="selectedTip" :sub-total="subTotal" class="shopping-cart-total__default-tip m-t-10 m-b-28">
            </default-tip-input>
          </template>
        </div>
      </div>

      <div class="shopping-cart-total__item shopping-cart-total__total total_item iw-body-text-small">
        <p>Total</p>
        <p class="iw-group-title">{{ prependUsdSign(total) }}</p>
      </div>

      <div class="shopping-cart-total__item shopping-cart-total__total total_item">
        <applied-amenities v-if="getAmenitiesInUse.amenities.length > 0 && readOnly" />
      </div>
    </div>

    <template v-if="!readOnly">
      <shopping-cart-promo v-if="!isConfirmation" />
      <app-button
        v-if="!isConfirmation"
        text="Checkout"
        :aria-label="'Checkout'"
        :handler="confirmCheckoutRequirements"
        :is-disabled="isEmptyCart || isCartInvalid"
      />
    </template>
  </div>
</template>

<!-- prettier-ignore -->
<script>

import { mapActions, mapGetters } from "vuex";
import { ERROR_PICK_UP_TIME_NO_LONGER_AVAILABLE } from "@/../app-constants";
import {
  showToaster,
  roundOffByBankApproach,
  OAPI_API_TYPE,
  MAPI_API_TYPE,
  MODAL,
  showDialog,
  ROUTE_NAMES,
  trackCheckoutView
} from "../../../../utils";
import TipInput from "./tip-input/TipInput";
import DefaultTipInput from "./default-tip-input/DefaultTipInput";
import AppliedAmenities from "./applied-amenities/AppliedAmenities";
import ShoppingCartPromo from "../cart-promo/ShoppingCartPromo";
import vendorDialogMixin from "@/mixins/vendor-dialog.mixin";
import RoundUpForCharity from "./round-up/RoundUpForCharity.vue";
import pickupTimeMixin from "@/mixins/pickup-time.mixin";
import CartItemsModal from "@/components/modal/cart-items-modal/CartItemsModal.vue";
import { prependUsdSign } from "@/utils";

export default {
  name: "ShoppingCartTotal",
  components: {
    TipInput,
    ShoppingCartPromo,
    DefaultTipInput,
    AppliedAmenities,
    RoundUpForCharity
  },
  mixins: [vendorDialogMixin, pickupTimeMixin],
  props: {
    isConfirmation: Boolean,
    cartItems: Array,
    readOnly: Boolean
  },
  data() {
    return {
      errors: [],
      isFormSubmitted: false,
      checkOutParams: null
    };
  },
  computed: {
    ...mapGetters({
      accentColor: "theme/getAccentColor",
      activeVendor: "vendor/getActiveVendor",
      getAmenitiesInUse: "cart/getAmenitiesWithCardBalanceInUse",
      appliedFee: "cart/getFeeValue",
      appliedTax: "cart/getTaxValue",
      appliedTip: "cart/getTipValue",
      discountedSubTotal: "cart/getDiscountedSubTotal",
      finalPrice: "cart/getFinalPrice",
      getCheckoutParams: "cart/getCheckoutParams",
      getOAPITotalDiscounts: "cart/getOAPITotalDiscounts",
      getSubTotal: "cart/getSubTotal",
      promoInfo: "cart/getPromoInfo",
      seatRequired: "venueSettings/seatRequired",
      selectedLevel: "delivery/getSelectedLevel",
      selectedRow: "delivery/getSelectedRow",
      selectedSeat: "delivery/getSelectedSeat",
      selectedSection: "delivery/getSelectedSection",
      venueInfo: "venue/getVenueInfo",
      getOAPIFeesDetail: "cart/getOAPIFeesDetail",
      getRoundUpForCharityAmount: "cart/getRoundUpForCharityAmount",
      getRoundUpForCharity: "cart/getRoundUpForCharity",
      isLoginAllowed: "venueSettings/isLoginAllowed",
      layout: "venueSettings/getLayout"
    }),
    subTotal: function () {
      return this.isConfirmation && this.getCheckoutParams
        ? this.getCheckoutParams.subtotalAmount
        : this.getSubTotal(this.cartItems);
    },
    total: function () {
      return roundOffByBankApproach(
        this.isConfirmation && this.getCheckoutParams ? this.getCheckoutParams.amount : this.finalPrice(this.cartItems)
      );
    },
    tipEnabled() {
      return this.activeVendor && this.activeVendor.tipEnabled;
    },
    selectedTip: {
      get() {
        return this.isConfirmation && this.getCheckoutParams ? this.getCheckoutParams.tip : this.appliedTip;
      },
      set(value) {
        this.applyTip(value);
      }
    },
    tax() {
      return this.isConfirmation && this.getCheckoutParams ? this.getCheckoutParams.tax : this.appliedTax();
    },
    getFeeText: function () {
      return (this.activeVendor.fee ? this.activeVendor.feeText : this.venueInfo.feeText) || "Fees";
    },
    fee: function () {
      return this.isConfirmation && this.getCheckoutParams
        ? this.getCheckoutParams.fee
        : this.appliedFee(this.discountedSubTotal ?? this.subTotal);
    },
    OAPIFeesDetail() {
      return this.isConfirmation && this.getCheckoutParams ? this.getCheckoutParams.OAPIFeesDetail : this.getOAPIFeesDetail;
    },
    isCartInvalid() {
      return this.cartItems.some((item) => !item.valid);
    },
    isEmptyCart() {
      return this.cartItems.length === 0;
    },
    getPromoAmount() {
      return (this.isConfirmation || this.readOnly) && this.getCheckoutParams && this.getCheckoutParams.promoInfo
        ? this.getCheckoutParams.promoInfo.originalSubtotal - this.getCheckoutParams.promoInfo.subtotal
        : 0;
    },
    defaultTipEnabled: function () {
      return this.venueInfo.shouldPreselectDefaultTip;
    },
    getOAPIDiscounts() {
      /*
      Discounts are returned as negatives from OAPI
      Need to multiply them by -1 so they are possitive and 
      Currency format can be applied
      */
      return this.isConfirmation && this.getCheckoutParams
        ? this.getCheckoutParams.OAPIDiscounts * -1
        : this.getOAPITotalDiscounts * -1;
    },
    enableCharityRounding() {
      if (!this.activeVendor || this.APIType === MAPI_API_TYPE) return false;
      if (this.activeVendor?.charityRoundingType.enabled) return true;
      if (this.getCheckoutParams?.roundUpForCharity) return true;
      return false;
    }
  },
  created() {
    this.OAPI_API_TYPE = OAPI_API_TYPE;
  },
  methods: {
    ...mapActions({
      checkUserIsAuthorized: "user/checkUserIsAuthorized",
      applyTip: "cart/applyTip",
      setCheckoutParams: "cart/setCheckoutParams",
      checkoutCreditCard: "cart/checkoutCreditCard",
      validateCart: "cart/validateCart",
      detectClosedVendors: "vendor/detectClosedVendors"
    }),
    prependUsdSign,
    checkout(params) {
      this.isFormSubmitted = true;
      return this.validateCart()
        .then(() => {
          return this.checkoutCreditCard({
            payments: [],
            fee: this.fee,
            tip: this.selectedTip,
            tax: this.tax,
            subtotalAmount: this.subTotal,
            amount: this.total
          });
        })
        .then(
          ({ order, data }) => {
            if (data && !data.success) {
              showToaster.call(this, {
                errorMessage: data.message || data.error
              });
            }

            //Track checkout event here after redirect to confirmation page.
            trackCheckoutView(params.cartItems);

            return this.$router.push({
              name: ROUTE_NAMES.ORDER_CONFIRMATION,
              params: {
                ...params,
                order
              }
            });
          },
          (reject) => {
            return this.errors.push(
              reject.response?.data?.message || reject.response?.data?.error || "Error processing checkout."
            );
          }
        );
    },
    async routeToCheckout() {
      try {
        await this.detectClosedVendors([this.activeVendor]);
        await this.checkVendorIsOpen();
      } catch {
        return;
      }

      const params = {
        subtotalAmount: this.subTotal,
        tip: this.selectedTip,
        tax: this.tax,
        fee: this.fee,
        amount: this.total,
        roundUpAmount: this.getRoundUpForCharityAmount,
        roundUpForCharity: this.getRoundUpForCharity,
        promoInfo: Object.assign({}, this.promoInfo),
        cartItems: [].concat(this.cartItems),
        OAPIDiscounts: this.getOAPITotalDiscounts,
        OAPIFeesDetail: this.getOAPIFeesDetail
      };

      this.setCheckoutParams(params);

      if (this.total === 0) {
        return this.checkUserIsAuthorized()
          .then(() => this.checkout(params))
          .catch(() => {
            //Route object to be passed to Login so it returns to shopping cart page and perform automatic checkout
            this.routeToIdentify({ target: ROUTE_NAMES.SHOPPING_CART, proceedToCheckout: "true" });
          });
      }

      return this.checkUserIsAuthorized()
        .then(() => this.$router.push({ name: ROUTE_NAMES.CHECKOUT }))
        .catch(() => this.routeToIdentify({ target: ROUTE_NAMES.CHECKOUT }));
    },
    routeToIdentify(params) {
      if (this.isLoginAllowed) {
        this.$router.push({
          name: ROUTE_NAMES.LOGIN,
          params: { ...params, continueAsGuest: "true" }
        });
        return;
      }

      this.$router.push({
        name: ROUTE_NAMES.CONTINUE_AS_GUEST,
        params
      });
    },
    async confirmCheckoutRequirements() {
      if (this.seatRequired && this.activeVendor.seatConfirmation) {
        showDialog(
          this,
          "Is this your seat?",
          ` <b>${this.layout.levelLabel}:</b> ${this.selectedLevel.name} <br> 
                                <b>${this.layout.sectionLabel}:</b> ${this.selectedSection.name} <br>
                                <b>${this.layout.rowLabel}:</b> ${this.selectedRow.name} <br>
                                <b>${this.layout.seatLabel}:</b> ${this.selectedSeat.name}
                            `,
          [
            {
              title: "Yes",
              handler: () => {
                this.continueWithSelectedSeat();
              }
            },
            {
              title: "No",
              handler: () => {
                this.changeSeat();
              }
            }
          ],
          false,
          true
        );
        return;
      }

     // When navigating to checkout from the shopping cart page, the selected pickup time might not be set
     // if navigating directly to a vendors menu page.  Instead of showing an error message to the time picker,
     // this check by-passes that check and only displays the time picker selection dialog.  If the selectedPickup
     // exists, then it will work as it always has and display an error message indicating that the selected time 
     // is no longer valid (likely in the past).
     const isPickupTimeValid = await this.isSelectedPickupTimeStillValid();
      if (!isPickupTimeValid) {
        return this.openSelectPickupTime(this.selectedPickup ? ERROR_PICK_UP_TIME_NO_LONGER_AVAILABLE : null);
      }

      return this.routeToCheckout();
    },
    continueWithSelectedSeat() {
      this.$modal.hide(MODAL.dialogName);
      this.routeToCheckout();
    },
    changeSeat() {
      this.$modal.hide(MODAL.dialogName);
      this.$router.push({ name: ROUTE_NAMES.VENUE_CONTAINER, params: { returnTo: ROUTE_NAMES.SHOPPING_CART } });
    },
    async openSelectPickupTime(errorMessage) {
      await this.showPickupTimeOptions(this.activeVendor.id, {
        errorMessage,
        goToNextStep: this.routeToCheckout
      });
    },
    showOrderItems() {
      this.$modal.show(CartItemsModal, {
        isUpFromBottomInMobile: true,
        cartItems: this.cartItems
      });
    }
  },
  mounted() {
    //If true then the user is on the shopping cart page
    if (!this.isConfirmation && !this.readOnly) {
      //If parm for automatic checkout was received
      if (this.$route.params.proceedToCheckout) {
        this.routeToCheckout();
      }
    }
  }
};
</script>

<style scoped lang="scss">
.shopping-cart-total {
  p {
    height: 20px;
    margin-block-start: 6px;
    margin-block-end: 6px;
  }

  &__header {
    align-items: center;
    display: flex;
    border-bottom: none;
    justify-content: space-between;

    a {
      font-size: 16px;
      color: $secondary-color;
      margin: 0;

      svg {
        transform: rotate(180deg);
      }
    }
  }

  &__item {
    display: flex;
    align-items: center;
    justify-content: space-between;
    border-bottom: none;
    font-weight: normal;
  }

  &__fee_item {
    display: flex;
    align-items: center;
    justify-content: space-between;
    border-bottom: none;
  }

  &__total {
    font-weight: bold;
    p {
      margin-block-end: 0px;
    }
  }

  &__default-tip {
    width: 100%;
  }

  &__link {
    outline: none;
    cursor: pointer;

    &:hover {
      text-decoration: underline;
    }
  }

  &__input {
    width: 65%;
  }

  &__promo-code-container {
    display: flex;
    justify-content: space-between;
    align-items: flex-end;
    margin: 2em 0;

    & > button {
      background-color: $background-dark--2;
      width: 25%;
      height: 3em;
      margin-bottom: 10px;
    }
  }
}
</style>
